import React, { FC, useCallback, useEffect, useState } from 'react';

import cn from 'clsx';
import { useSnackbar } from 'notistack';
import styled from 'styled-components';

import { Button, Link, Stack, TextField } from '@mui/material';
import { DataGrid, DataGridProps, GridColDef, GridFilterOperator } from '@mui/x-data-grid';

import { getTrips } from '~/services/TripPlannerService';

const StyledDataGrid = styled(DataGrid)`
  &.no-trips {
    .MuiDataGrid-overlayWrapper {
      height: 50px;
    }
    .MuiDataGrid-overlayWrapperInner {
      height: 50px;
    }
  }
` as FC<DataGridProps>;

const tripGptPromptsOperators: Array<GridFilterOperator> = [
  {
    label: 'Has Prompts',
    value: 'hasPrompts',
    getApplyFilterFn: (_filterItem) => {
      return (value) => !!value;
    },
  },
  {
    label: 'No Prompts',
    value: 'noPrompts',
    getApplyFilterFn: (_filterItem) => {
      return (value) => !value;
    },
  },
];

export default function TripPlannerContent() {
  // table data
  const [trips, setTrips] = useState<Array<App.AdminTripDetails>>([]);
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
  }>({
    pageSize: 20,
    page: 0,
  });
  const [totalTrips, setTotalTrips] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const [tripId, setTripId] = useState<string>('');
  const [orderId, setOrderId] = useState<string>('');
  const [cartId, setCartId] = useState<string>('');

  const [searchParams, setSearchParams] = useState<{
    tripId: string | undefined;
    orderId: string | undefined;
    cartId: string | undefined;
  }>({ tripId: undefined, orderId: undefined, cartId: undefined });

  const { enqueueSnackbar } = useSnackbar();

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTrips({
        page: paginationModel.page + 1,
        tripId: searchParams.tripId ? searchParams.tripId.trim() : undefined,
        orderId: searchParams.orderId ? searchParams.orderId.trim() : undefined,
        cartId: searchParams.cartId ? searchParams.cartId.trim() : undefined,
      });
      if (response.status === 200) {
        setTrips(response.result.trips);
        setTotalTrips(response.result.numberOfTrips);
      }
    } catch {
      setTrips([]);
      setTotalTrips(0);
      enqueueSnackbar(
        'Invalid Trip ID, Order ID or Cart ID. Please ensure the IDs are entered correctly and try again.',
        {
          variant: 'error',
        },
      );
    }
    setLoading(false);
  }, [enqueueSnackbar, paginationModel, searchParams]);

  useEffect(() => {
    fetchTrips(); // Fetch trips whenever pagination or search parameters change

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, searchParams]);

  const handleSearch = () => {
    setPaginationModel({
      pageSize: 20,
      page: 0,
    });
    setSearchParams({
      tripId,
      orderId,
      cartId,
    });
  };

  const columns: Array<GridColDef> = [
    { field: 'name', headerName: 'Name', width: 150 },
    {
      field: 'createdAt',
      headerName: 'Created At (UTC)',
      width: 200,
      valueFormatter: (value: string) => {
        return value ? new Date(value).toLocaleString('en-GB', { timeZone: 'UTC' }) : '';
      },
    },
    { field: 'id', headerName: 'Trip Id', width: 200 },
    {
      field: 'createdBy',
      headerName: 'User',
      width: 200,
      renderCell: (params) => {
        return (
          <Link color="primary" rel="noreferrer" href={`/users/${params.row.createdBy}`} underline="hover">
            View User
          </Link>
        );
      },
      display: 'flex',
    },
    {
      field: 'openTrip',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <Link color="primary" rel="noreferrer" href={`/trip-planner/trip/${params.row.id}`} underline="hover">
          Open Trip
        </Link>
      ),
      display: 'flex',
    },
    {
      field: 'hasDebugData',
      headerName: 'Trip GPT Prompts',
      width: 200,
      renderCell: (params) => {
        if (!params.value) {
          return null;
        }
        return (
          <Link
            color="primary"
            rel="noreferrer"
            href={`/trip-planner/trip/${params.row.id}/prompts/details`}
            underline="hover"
          >
            See trip prompts
          </Link>
        );
      },
      filterOperators: tripGptPromptsOperators,
      type: 'boolean',
      display: 'flex',
    },
  ];

  return (
    <>
      <Stack spacing={2} direction="row" mb={2}>
        <TextField
          label="Trip Id"
          variant="outlined"
          value={tripId}
          fullWidth
          onChange={(e) => setTripId(e.target.value)}
        />
        <TextField
          label="Order Id"
          variant="outlined"
          value={orderId}
          fullWidth
          onChange={(e) => setOrderId(e.target.value)}
        />
        <TextField
          label="Cart Id"
          variant="outlined"
          value={cartId}
          fullWidth
          onChange={(e) => setCartId(e.target.value)}
        />
      </Stack>

      <Stack spacing={2} direction="row" mb={2} justifyContent="space-between">
        <Button variant="contained" color="primary" onClick={handleSearch}>
          Search
        </Button>
      </Stack>
      <StyledDataGrid
        rows={trips}
        columns={columns}
        pagination
        paginationMode="server"
        rowCount={totalTrips}
        pageSizeOptions={[]}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        localeText={{
          noRowsLabel: 'No trips found',
        }}
        getRowHeight={() => 'auto'}
        className={cn({ 'no-trips': trips.length === 0 })}
      />
    </>
  );
}
